import React, { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { setUserParam } from 'actions';

const SliderContainer = styled.div`
  position: fixed;
  background: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 10000;
  min-width: 400px;
  user-select: none;
`;

const DragHandle = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  margin-bottom: 0.5rem;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid #eee;

  &:active {
    cursor: grabbing;
  }
`;

const DragHandleLabel = styled.span`
  font-size: 0.9rem;
  color: #000;
  letter-spacing: 0.05em;
  font-weight: bold;
  text-align: center;
`;

const DragHandleDots = styled.span`
  position: absolute;
  right: 0;
  font-size: 1rem;
  color: #ccc;
  letter-spacing: 3px;
  line-height: 1;
`;

const SliderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Slider = styled.input`
  width: 100%;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #007acc;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #007acc;
    cursor: pointer;
    border: none;
  }
`;

const TimeDisplay = styled.div`
  font-size: 1rem;
  color: #007acc;
  font-weight: 700;
  text-align: center;
`;

const TrafficTimeline = ({
  trafficMatrix,
  selectedTimestamp,
  onTimestampChange,
  firstStopDeparture,
}) => {
  const dispatch = useDispatch();

  const [position, setPosition] = useState(() => ({
    x: window.innerWidth / 2 - 200,
    y: window.innerHeight - 160,
  }));
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const onDragStart = useCallback(
    (e) => {
      dragging.current = true;
      dragOffset.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };

      const onMouseMove = (ev) => {
        if (!dragging.current) return;
        const newX = Math.max(
          0,
          Math.min(window.innerWidth - 420, ev.clientX - dragOffset.current.x),
        );
        const newY = Math.max(
          0,
          Math.min(window.innerHeight - 140, ev.clientY - dragOffset.current.y),
        );
        setPosition({ x: newX, y: newY });
      };

      const onMouseUp = () => {
        dragging.current = false;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [position],
  );

  if (!trafficMatrix?.factorsData?.matrix?.timestamps) {
    return null;
  }

  const { timestamps } = trafficMatrix.factorsData.matrix;
  const currentIndex = timestamps.findIndex((ts) => ts === selectedTimestamp);

  const handleSliderChange = useCallback(
    (e) => {
      const index = parseInt(e.target.value, 10);
      const newTimestamp = timestamps[index];

      // Offset is relative to the first stop's actual departure time so that
      // offset=0 means "use real segment departure times" and the slider
      // starts at the tour's real departure (not at timestamps[0] which is -4h buffer).
      const baseTime = firstStopDeparture
        ? new Date(firstStopDeparture).getTime()
        : new Date(timestamps[0]).getTime();
      const offsetMs = new Date(newTimestamp).getTime() - baseTime;
      dispatch(setUserParam({ trafficTimeOffset: offsetMs }));

      onTimestampChange(newTimestamp);
    },
    [timestamps, dispatch, onTimestampChange, firstStopDeparture],
  );

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <SliderContainer style={{ left: position.x, top: position.y }}>
      <DragHandle onMouseDown={onDragStart}>
        <DragHandleLabel>Departure Time</DragHandleLabel>
        <DragHandleDots>⋮⋮⋮</DragHandleDots>
      </DragHandle>
      <SliderWrapper>
        <TimeDisplay>
          {formatDate(selectedTimestamp)} {formatTimestamp(selectedTimestamp)}
        </TimeDisplay>
        <Slider
          type="range"
          min={0}
          max={timestamps.length - 1}
          value={currentIndex !== -1 ? currentIndex : 0}
          onChange={handleSliderChange}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '0.75rem',
            color: '#666',
          }}
        >
          <span>{formatTimestamp(timestamps[0])}</span>
          <span>{formatTimestamp(timestamps[timestamps.length - 1])}</span>
        </div>
      </SliderWrapper>
    </SliderContainer>
  );
};

export default TrafficTimeline;
